import React, { useRef, useEffect, useState } from "react";
import { Card, CardContent, Typography, useMediaQuery, useTheme, Box } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Grid } from "@mui/system";
import LaptopOutlinedIcon from "@mui/icons-material/LaptopOutlined";
import { motion, useInView, useAnimation } from "framer-motion";

// Dil bazlı dinamik proje açıklamaları ve genel metinler
const translations = {
  tr: {
    sectionTitle: "Öne Çıkan Projeler",
    chefai: "Modern web teknolojileri kullanarak tarifler, öğün planları ve akıllı malzeme tabanlı öneriler sunan tam donanımlı, yapay zeka destekli bir mutfak asistanı geliştirildi.",
    stellarlab: "Resmi NASA API'lerini kullanarak Dünya'nın 10 yıllık yörünge arşivini, gerçek zamanlı Mars Rover telemetrisini ve derin uzay güneş aktivitesi monitörünü içeren gelişmiş bir uzay keşif platformu.",
    moodflix: "Kullanıcılara kişiselleştirilmiş öneriler, gerçek zamanlı film verileri ve sosyal özellikler sunan yapay zeka destekli bir film keşif uygulaması inşa edildi.",
    lostandfound: "Kullanıcıların kayıp eşyalarını veya evcil hayvanlarını ilan etmelerine, aramalarına ve bulmalarına yardımcı olmak için tam donanımlı bir kayıp-buluntu web uygulaması geliştirildi.",
    dashboard: "Gerçek zamanlı stok takibi, otomatik uyarılar ve gelişmiş veri görselleştirmeleri içeren profesyonel bir envanter yönetim paneli geliştirildi.",
    diaryai: "Kullanıcıların günlük deneyimlerini ve duygusal durumlarını analiz eden, yapay zeka destekli kişiselleştirilmiş bir günlük uygulaması.",
    movieapp: "Kullanıcıların kişiselleştirilmiş önerilerle filmler bulmasını sağlayan bir sinema uygulaması."
  },
  en: {
    sectionTitle: "Featured Projects",
    chefai: "Developed a full-stack AI-powered cooking assistant that suggests recipes, meal plans, and smart ingredient-based recommendations using modern web technologies.",
    stellarlab: "An advanced space exploration platform featuring a 10-year orbital archive of Earth, real-time Mars Rover telemetry, and a deep-space solar activity monitor using official NASA APIs.",
    moodflix: "Built an AI-powered movie discovery app that provides personalized recommendations, real-time movie data, and social features.",
    lostandfound: "Developed a full-stack lost and found web application to help users post, search, and find lost items or pets.",
    dashboard: "Developed a professional inventory dashboard featuring real-time stock tracking, automated alerts, and advanced data visualizations.",
    diaryai: "A personalized AI-powered app that analyzes users' daily experiences and emotional states.",
    movieapp: "A movie application that allows users to find movies with personalized suggestions."
  }
};

// Statik proje verileri (Açıklamalar dile göre translation nesnesinden dinamik çekilecek)
const servicesData = [
  {
    title: "ChefAI",
    translationKey: "chefai",
    icon: <LaptopOutlinedIcon />,
    link: "https://play.google.com/store/apps/details?id=com.idrisaktas.chefai&pcampaignid=web_share",
  },
  {
    title: "StellarLab (NASA App)",
    translationKey: "stellarlab",
    icon: <LaptopOutlinedIcon />,
    link: "https://nasa-app-client-u3n8.onrender.com/",
  },
  {
    title: "Moodflix AI",
    translationKey: "moodflix",
    icon: <LaptopOutlinedIcon />,
    link: "https://play.google.com/store/apps/details?id=com.idrisaktas.aimovie&pcampaignid=web_share",
  },
  {
    title: "Lost and Found",
    translationKey: "lostandfound",
    icon: <LaptopOutlinedIcon />,
    link: "https://www.lostandfound.website/",
  },
  {
    title: "Dashboard",
    translationKey: "dashboard",
    icon: <LaptopOutlinedIcon />,
    link: "https://smart-dashboard-opal.vercel.app/",
  },
  {
    title: "Diary AI",
    translationKey: "diaryai",
    icon: <LaptopOutlinedIcon />,
    link: "https://ai-diary-zeta.vercel.app/",
  },
  {
    title: "Movie App",
    translationKey: "movieapp",
    icon: <LaptopOutlinedIcon />,
    link: "https://www.cinescope.live/",
  }
];

const ServiceCard = ({ title, description, icon, link, index, activeIndex }) => {
  const cardRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const isInView = useInView(cardRef, { once: true });
  const controls = useAnimation();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const handleCardClick = () => {
    window.open(link, "_blank");
  };

  useEffect(() => {
    if (activeIndex === index && !hovered) {
      controls.start({
        scale: [1, 1.07, 1],
        transition: { duration: 0.8 }
      });
    }
  }, [activeIndex, controls, index, hovered]);

  return (
    <Grid
      item
      size={{ xs: 12, sm: 8, md: 5, lg: 2.6, xl: 2.6 }}
      ref={cardRef}
      sx={{
        cursor: isMdUp ? "pointer" : "default",
      }}
      onClick={isMdUp ? handleCardClick : null}
    >
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 1, delay: index * 0.2 }}
      >
        <motion.div animate={controls}>
          <Card
            sx={{
              backgroundColor: hovered ? "#0b260b" : "transparent",
              p: 2,
              border: "2px solid rgb(0, 0, 0)",
              borderRadius: 3,
              boxShadow: 3,
              transition: "transform 0.3s, background-color 0.6s",
              position: "relative",
              minHeight: 200,
              height: hovered ? "max-content" : 200,
              overflow: "hidden",
              "&:hover": { transform: "scale(1.02)" },
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundImage: "url('/practices.jpg')",
                backgroundSize: hovered ? "180%" : "cover",
                backgroundPosition: "center",
                opacity: hovered ? 0 : 0.6,
                transition: "background-size 0.6s ease, opacity 0.6s ease",
                zIndex: 1,
              }}
            />

            <CardContent
              sx={{
                height: "10px",
                mt: hovered ? 2 : 0,
                position: "relative",
                top: hovered ? "10%" : "45%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                color: "white",
                zIndex: 2,
                transition: "top 0.3s",
              }}
            >
              <Typography
                variant="H32px"
                color="textThird"
                align="center"
                bgcolor={hovered ? "none" : "#305041b8"}
                border={hovered ? "none" : "2px solid #305041"}
                borderRadius={hovered ? "none" : "10px"}
                padding={hovered ? "0" : "4px"}
              >
                {title}
              </Typography>
            </CardContent>

            <motion.div
              style={{
                display: hovered ? "flex" : "none",
                zIndex: 3,
                gap: 10,
                flexDirection: "column",
                textAlign: "center",
                alignItems: "center",
                height: "100%",
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: hovered ? 1 : 0 }}
              transition={{ duration: 0.5 }}
            >
              <Typography variant="body1" align="center" color="white">
                {description}
              </Typography>

              <Box
                borderRadius={"100%"}
                bgcolor={"#61a05f"}
                padding={"8px 8px 6px 10px"}
                color={"#0b260b"}
                onClick={handleCardClick}
                sx={{
                  cursor: "pointer",
                  zIndex: 4,
                }}
              >
                <ArrowForwardIosIcon />
              </Box>
            </motion.div>
          </Card>
        </motion.div>
      </motion.div>
    </Grid>
  );
};

const Services = ({ currentLang = "tr" }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  // Gelen dil verisini normalize et ve translation'ları yakala
  const normalizedLang = String(currentLang).toLowerCase();
  const t = translations[normalizedLang] || translations.tr;

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % servicesData.length);
    }, 1200);

    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: "#305041",
        padding: { xs: "35px 10px 45px 10px", md: "50px", lg: "50px 10px 50px 10px" },
      }}
    >
      <Typography variant="H38px" align="center" color="textThird">
        {t.sectionTitle}
      </Typography>

      <Grid
        mt={1}
        justifySelf={"center"}
        mb={6}
        sx={{
          backgroundColor: "#61a05f",
          width: "120px",
          height: "2px",
          position: "relative",
          overflow: "hidden",
          "::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: "-120px",
            width: "120px",
            height: "2px",
            background: "linear-gradient(to right, transparent, #fff, transparent)",
            animation: "moveLight 2.5s infinite",
          },
        }}
      >
        <style>
          {`
            @keyframes moveLight {
              0% { left: -120px; }
              100% { left: 120px; }
            }
          `}
        </style>
      </Grid>

      <Grid container gap={3} justifyContent="center">
        {servicesData.map((service, index) => (
          <ServiceCard
            key={index}
            index={index}
            activeIndex={activeIndex}
            title={service.title}
            // Çeviriyi dinamik olarak translation anahtarına göre buradaki dilden alıp gönderiyoruz
            description={t[service.translationKey]}
            icon={service.icon}
            link={service.link}
          />
        ))}
      </Grid>
    </Box>
  );
};

export default Services;